import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CallState, CommunityStructure, StreamSettings, VoiceJoinResult, VoiceParticipant, VoiceSignal } from "@atrium/shared";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { getPrefs } from "../../lib/prefs";
import { getSocket } from "../../lib/socket";
import { toast } from "../../components/ui/Toast";
import { Mesh } from "./mesh";
import { useCallStore, useStreamUi, useVoiceStatesStore } from "./stores";
import { IncomingCalls } from "./IncomingCall";
import { GoLiveDialog } from "./GoLiveDialog";
import { MiniPlayer } from "./MiniPlayer";
import { contentHintFor, displayMediaOptions, loadStreamChoice, saveStreamChoice, snapshot, videoConstraints, type StreamChoice } from "./stream";

export { useVoiceStates, useStageTopic, useCall, useCallStore, useStreamUi } from "./stores";

type Status = "idle" | "connecting" | "connected";

/** The one voice room you're in: a voice channel, a stage, or a call in a conversation. */
export interface VoiceRoom {
  kind: "voice" | "stage" | "call";
  /** Channel id, or conversation id for calls. */
  id: string;
  communityId: string | null;
  name: string;
}

interface VoiceCtx {
  room: VoiceRoom | null;
  /** Voice or stage channel you're in (null in calls). */
  channelId: string | null;
  communityId: string | null;
  channelName: string | null;
  /** Conversation whose call you're in. */
  callId: string | null;
  status: Status;
  muted: boolean;
  deafened: boolean;
  /** Muted by a moderator, lacking Speak, or listening from a stage's audience; can't unmute. */
  serverMuted: boolean;
  /** Push-to-talk key currently held. */
  talking: boolean;
  speaking: Set<string>;
  selfSocketId: string | null;
  /** Everyone in your room, as last pushed by the server. */
  participants: VoiceParticipant[];
  self: VoiceParticipant | null;
  /** Your own screen share, for the preview tile. */
  localScreen: MediaStream | null;
  /** Your live stream's settings (null when not streaming). */
  shareSettings: StreamSettings | null;
  /** The live stream has an audio track that could be switched on or off. */
  shareHasAudio: boolean;
  /** Remote screens you're receiving, by socket id. */
  screens: Record<string, MediaStream>;
  /** Streams you've chosen to watch, by socket id. */
  watching: ReadonlySet<string>;
  setWatching: (socketId: string, on: boolean) => void;
  join: (channelId: string, communityId: string, channelName: string) => Promise<void>;
  joinCall: (conversationId: string, name: string) => Promise<void>;
  leave: () => void;
  toggleMute: () => void;
  toggleDeafen: () => void;
  /** Open the browser's picker and go live. Call straight from a click: browsers require a user gesture. */
  startShare: (choice: StreamChoice) => Promise<void>;
  /** Pick a different screen, window or tab without ending the stream. */
  changeShareSource: () => Promise<void>;
  /** Change quality (and switch captured audio on or off) while live. */
  updateShare: (choice: StreamChoice) => Promise<void>;
  stopShare: () => void;
}

const Ctx = createContext<VoiceCtx | null>(null);

let iceServersPromise: Promise<RTCIceServer[]> | null = null;
const getIceServers = () => {
  iceServersPromise ??= api
    .get<{ iceServers: RTCIceServer[] }>("/api/config")
    .then((c) => c.iceServers)
    .catch(() => [{ urls: "stun:stun.l.google.com:19302" }]);
  return iceServersPromise;
};

export const VOICE_INPUT_KEY = "atrium:voice:input";
export const VOICE_OUTPUT_KEY = "atrium:voice:output";

/** How often a live stream sends a fresh snapshot for people who aren't watching. */
const PREVIEW_EVERY_MS = 10_000;

/** Cancelling the browser's picker isn't an error. */
const pickerCancelled = (e: unknown) => ["NotAllowedError", "AbortError"].includes((e as DOMException)?.name);

export const canShareScreen = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

function matchesKey(e: KeyboardEvent, bind: string) {
  const parts = bind.split("+");
  const key = parts.pop() ?? "";
  const want = { ctrl: parts.includes("Ctrl"), shift: parts.includes("Shift"), alt: parts.includes("Alt") };
  if (e.ctrlKey !== want.ctrl || e.shiftKey !== want.shift || e.altKey !== want.alt) return false;
  return e.key.toLowerCase() === key.toLowerCase() || (key === "Space" && e.code === "Space");
}

type JoinAck = (res: { ok: true; data?: VoiceJoinResult } | { ok: false; error: string }) => void;

/**
 * Peer-to-peer voice over WebRTC. The server relays signalling only; media
 * never passes through it. See mesh.ts for the connection details.
 */
export function VoiceProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [room, setRoom] = useState<VoiceRoom | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [serverMuted, setServerMuted] = useState(false);
  const [talking, setTalking] = useState(false);
  const [speaking, setSpeaking] = useState<Set<string>>(new Set());
  const [participants, setParticipants] = useState<VoiceParticipant[]>([]);
  const [localScreen, setLocalScreen] = useState<MediaStream | null>(null);
  const [shareSettings, setShareSettings] = useState<StreamSettings | null>(null);
  const [screens, setScreens] = useState<Record<string, MediaStream>>({});
  const [watching, setWatchingState] = useState<ReadonlySet<string>>(new Set());

  const roomRef = useRef<VoiceRoom | null>(null);
  const mesh = useRef<Mesh | null>(null);
  const mic = useRef<MediaStream | null>(null);
  const screen = useRef<MediaStream | null>(null);
  const shareRef = useRef<StreamSettings | null>(null);
  const localAnalyser = useRef<AnalyserNode | null>(null);
  const peerAnalysers = useRef(new Map<string, AnalyserNode>());
  const audioCtx = useRef<AudioContext | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const serverMutedPeers = useRef(new Set<string>());
  const participantsRef = useRef<VoiceParticipant[]>([]);
  const watchingRef = useRef(new Set<string>());
  const sentWatch = useRef(new Set<string>());
  const wasStreaming = useRef(new Map<string, boolean>());
  const wasSpeaker = useRef(false);
  const state = useRef({ muted, deafened, serverMuted, talking });

  /** Whether our microphone track should be live right now. */
  const micLive = () => {
    const st = state.current;
    if (st.muted || st.deafened || st.serverMuted) return false;
    return getPrefs(qc).pushToTalk ? st.talking : true;
  };
  const applyMic = () => mic.current?.getAudioTracks().forEach((t) => (t.enabled = micLive()));
  const setMutedBoth = (v: boolean) => {
    state.current.muted = v;
    setMuted(v);
  };

  const ensureAudioCtx = () => {
    audioCtx.current ??= new AudioContext();
    if (audioCtx.current.state === "suspended") void audioCtx.current.resume();
    return audioCtx.current;
  };
  const makeAnalyser = (stream: MediaStream) => {
    try {
      const ctx = ensureAudioCtx();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      return an;
    } catch {
      return undefined;
    }
  };

  const getMic = async () => {
    const prefs = getPrefs(qc);
    const deviceId = localStorage.getItem(VOICE_INPUT_KEY);
    const constraints = { echoCancellation: prefs.echoCancellation, noiseSuppression: prefs.noiseSuppression, autoGainControl: prefs.autoGainControl };
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: deviceId ? { ...constraints, deviceId: { exact: deviceId } } : constraints });
    } catch (err) {
      // The saved device may be gone; fall back to the default microphone.
      if (!deviceId) throw err;
      return navigator.mediaDevices.getUserMedia({ audio: constraints });
    }
  };

  const stopScreenLocal = useCallback(() => {
    const s = screen.current;
    if (!s) return false;
    screen.current = null;
    shareRef.current = null;
    mesh.current?.setScreen(null);
    mesh.current?.clearScreenWanted();
    s.getTracks().forEach((t) => t.stop());
    setLocalScreen(null);
    setShareSettings(null);
    return true;
  }, []);

  const cleanup = useCallback(() => {
    roomRef.current = null;
    mesh.current?.closeAll();
    mesh.current = null;
    stopScreenLocal();
    mic.current?.getTracks().forEach((t) => t.stop());
    mic.current = null;
    localAnalyser.current = null;
    peerAnalysers.current.clear();
    serverMutedPeers.current.clear();
    participantsRef.current = [];
    watchingRef.current = new Set();
    sentWatch.current.clear();
    wasStreaming.current.clear();
    wasSpeaker.current = false;
    state.current.serverMuted = false;
    state.current.talking = false;
    setSpeaking(new Set());
    setServerMuted(false);
    setTalking(false);
    setParticipants([]);
    setScreens({});
    setWatchingState(new Set());
    useStreamUi.getState().setFocused(null);
    setStatus("idle");
    setRoom(null);
  }, [stopScreenLocal]);

  /** Tell streamers whether we want their screen, to match `watchingRef`. */
  const syncWatch = useCallback(() => {
    const s = getSocket();
    const list = participantsRef.current;
    for (const p of list) {
      if (p.socketId === s?.id) continue;
      const want = p.streaming && watchingRef.current.has(p.socketId);
      if (want === sentWatch.current.has(p.socketId)) continue;
      s?.emit("voice:signal", { to: p.socketId, data: { type: "watch", on: want } });
      if (want) sentWatch.current.add(p.socketId);
      else sentWatch.current.delete(p.socketId);
    }
    const present = new Set(list.map((p) => p.socketId));
    for (const id of [...sentWatch.current]) if (!present.has(id)) sentWatch.current.delete(id);
  }, []);

  const ensureMic = useCallback(async () => {
    if (mic.current) {
      mesh.current?.setMic(mic.current);
      return;
    }
    const r = roomRef.current;
    try {
      const stream = await getMic();
      if (roomRef.current !== r || !r) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      mic.current = stream;
      localAnalyser.current = makeAnalyser(stream) ?? null;
      applyMic();
      mesh.current?.setMic(stream);
    } catch {
      toast.error("Microphone access was blocked. Allow it in your browser to speak.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** The server's view of our room changed: mutes, stage role, streams, and who to connect to. */
  const applyRoomState = useCallback(
    (list: VoiceParticipant[]) => {
      const s = getSocket();
      const r = roomRef.current;
      if (!s?.id || !r) return;
      participantsRef.current = list;
      setParticipants(list);
      const self = list.find((x) => x.socketId === s.id);
      if (!self) return;

      // Server mutes are enforced by listeners: we stop playing a muted peer's audio.
      serverMutedPeers.current = new Set(list.filter((x) => x.serverMuted).map((x) => x.socketId));
      mesh.current?.applySilence();

      if (r.kind === "stage") {
        const speaker = !!self.speaker;
        if (speaker !== wasSpeaker.current) {
          wasSpeaker.current = speaker;
          if (speaker) {
            // The server starts new speakers muted.
            setMutedBoth(true);
            void ensureMic();
            toast.show("You're on stage. Unmute when you're ready to speak.");
          } else {
            if (stopScreenLocal()) getSocket()?.emit("voice:stream", { streaming: false }, () => {});
            toast.show("You're in the audience");
          }
        }
      }
      if (self.serverMuted !== state.current.serverMuted) {
        state.current.serverMuted = self.serverMuted;
        setServerMuted(self.serverMuted);
        if (self.serverMuted && !(r.kind === "stage" && !self.speaker)) toast.show("You've been muted in this channel");
      }
      applyMic();

      // Losing Share screen (or the stage) ends our stream on the server; follow it.
      if (screen.current && !self.streaming && stopScreenLocal()) toast.show("Screen sharing stopped");

      // Streams: auto-watch in calls and on stage; in voice channels it's opt-in.
      const auto = r.kind !== "voice";
      const next = new Map<string, boolean>();
      for (const p of list) {
        if (p.socketId === s.id) continue;
        next.set(p.socketId, p.streaming);
        if (p.streaming && !wasStreaming.current.get(p.socketId) && auto) watchingRef.current.add(p.socketId);
        if (!p.streaming && watchingRef.current.delete(p.socketId)) mesh.current?.dropRemoteScreen(p.socketId);
      }
      for (const id of [...watchingRef.current]) if (!next.has(id)) watchingRef.current.delete(id);
      wasStreaming.current = next;
      const ui = useStreamUi.getState();
      if (ui.focused && !list.some((p) => p.socketId === ui.focused && p.streaming)) ui.setFocused(null);
      setWatchingState(new Set(watchingRef.current));
      syncWatch();

      // Connect to everyone, except that a stage's audience only connects to speakers.
      const desired = new Set(list.filter((p) => p.socketId !== s.id && (r.kind !== "stage" || self.speaker || p.speaker)).map((p) => p.socketId));
      mesh.current?.reconcile(desired);
      for (const id of [...peerAnalysers.current.keys()]) if (!desired.has(id)) peerAnalysers.current.delete(id);
    },
    [ensureMic, stopScreenLocal, syncWatch], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Signalling, room and call events.
  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const onSignal = ({ from, data }: { from: string; data: VoiceSignal }) => {
      if (roomRef.current) mesh.current?.handleSignal(from, data);
    };
    const onPeerLeft = ({ channelId, socketId }: { channelId: string; socketId: string }) => {
      if (roomRef.current?.id === channelId) mesh.current?.close(socketId);
    };
    const onKicked = ({ channelId, reason }: { channelId: string; reason: string }) => {
      const r = roomRef.current;
      if (!r || r.id !== channelId) return;
      cleanup();
      if (r.kind === "call") toast.show(reason);
      else toast.error(reason);
    };
    const onState = (p: { communityId: string; channelId: string; participants: VoiceParticipant[]; topic?: string }) => {
      useVoiceStatesStore.getState().setChannel(p.communityId, p.channelId, p.participants, p.topic);
      const r = roomRef.current;
      if (r && r.kind !== "call" && r.id === p.channelId) applyRoomState(p.participants);
    };
    const onCallState = ({ conversationId, call }: { conversationId: string; call: CallState | null }) => {
      useCallStore.getState().setCall(conversationId, call);
      const r = roomRef.current;
      if (r?.kind !== "call" || r.id !== conversationId) return;
      if (call) applyRoomState(call.participants);
      else {
        cleanup();
        toast.show("The call ended");
      }
    };
    const onRing = (p: { conversationId: string; from: VoiceParticipant["user"]; call: CallState }) => {
      if (roomRef.current?.kind === "call" && roomRef.current.id === p.conversationId) return;
      useCallStore.getState().ring({ ...p, at: Date.now() });
    };
    const onRingEnd = ({ conversationId }: { conversationId: string }) => useCallStore.getState().stopRing(conversationId);
    // Calls are re-sent by the server on every connection.
    const onConnect = () => useCallStore.getState().reset();
    const onDisconnect = () => {
      if (roomRef.current) {
        cleanup();
        toast.error("Voice disconnected");
      }
    };
    s.on("voice:signal", onSignal);
    s.on("voice:peer-left", onPeerLeft);
    s.on("voice:kicked", onKicked);
    s.on("voice:state", onState);
    s.on("call:state", onCallState);
    s.on("call:ring", onRing);
    s.on("call:ring-end", onRingEnd);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    return () => {
      s.off("voice:signal", onSignal);
      s.off("voice:peer-left", onPeerLeft);
      s.off("voice:kicked", onKicked);
      s.off("voice:state", onState);
      s.off("call:state", onCallState);
      s.off("call:ring", onRing);
      s.off("call:ring-end", onRingEnd);
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
    };
  }, [applyRoomState, cleanup]);

  // Speaking indicators.
  useEffect(() => {
    if (status !== "connected") return;
    const buf = new Uint8Array(512);
    const level = (an?: AnalyserNode | null) => {
      if (!an) return 0;
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += (v - 128) * (v - 128);
      return Math.sqrt(sum / buf.length);
    };
    const t = setInterval(() => {
      const next = new Set<string>();
      const self = getSocket()?.id;
      if (self && micLive() && level(localAnalyser.current) > 6) next.add(self);
      for (const [id, an] of peerAnalysers.current) if (!serverMutedPeers.current.has(id) && level(an) > 6) next.add(id);
      setSpeaking((prev) => (prev.size === next.size && [...next].every((x) => prev.has(x)) ? prev : next));
    }, 150);
    return () => clearInterval(t);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => cleanup(), [cleanup]);

  const leave = useCallback(() => {
    if (!roomRef.current) return;
    getSocket()?.emit("voice:leave");
    cleanup();
  }, [cleanup]);

  /** Shared join flow: leave any other room, get the microphone, set up the mesh, then ask the server. */
  const enter = useCallback(
    async (r: VoiceRoom, needMic: boolean, emitJoin: (ack: JoinAck) => void) => {
      const s = getSocket();
      if (!s?.connected || !s.id) {
        toast.error("You're offline. Voice will be available when you reconnect.");
        return;
      }
      if (needMic && !navigator.mediaDevices?.getUserMedia) {
        toast.error("Voice needs a browser with microphone support over HTTPS or localhost.");
        return;
      }
      if (roomRef.current) leave();
      roomRef.current = r;
      setRoom(r);
      setStatus("connecting");
      if (getPrefs(qc).joinMuted) setMutedBoth(true);
      const iceServers = await getIceServers();
      let stream: MediaStream | null = null;
      if (needMic) {
        try {
          stream = await getMic();
        } catch {
          if (roomRef.current === r) cleanup();
          toast.error("Microphone access was blocked. Allow it in your browser to join voice.");
          return;
        }
      }
      if (roomRef.current !== r || !s.id) {
        stream?.getTracks().forEach((t) => t.stop());
        return;
      }
      mic.current = stream;
      localAnalyser.current = stream ? (makeAnalyser(stream) ?? null) : null;
      applyMic();
      mesh.current = new Mesh({
        selfId: s.id,
        iceServers,
        send: (to, data) => getSocket()?.emit("voice:signal", { to, data }),
        container: container.current,
        outputDeviceId: localStorage.getItem(VOICE_OUTPUT_KEY),
        isSilenced: (id) => state.current.deafened || serverMutedPeers.current.has(id),
        onScreen: (id, st) =>
          setScreens((prev) => {
            if (st && prev[id] === st) return { ...prev };
            const next = { ...prev };
            if (st) next[id] = st;
            else delete next[id];
            return next;
          }),
        onMicStream: (id, st) => {
          const an = makeAnalyser(st);
          if (an) peerAnalysers.current.set(id, an);
        },
      });
      mesh.current.setMic(stream);
      emitJoin((res) => {
        if (roomRef.current !== r) {
          // Left while the join was in flight: make sure the server agrees (unless another join is pending).
          if (res.ok && !roomRef.current) getSocket()?.emit("voice:leave");
          return;
        }
        if (!res.ok) {
          cleanup();
          toast.error(res.error);
          return;
        }
        setStatus("connected");
        getSocket()?.emit("voice:state", { muted: state.current.muted, deafened: state.current.deafened });
      });
    },
    [cleanup, leave], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const join = useCallback(
    async (channelId: string, communityId: string, name: string) => {
      const type = qc.getQueryData<CommunityStructure>(["structure", communityId])?.channels.find((c) => c.id === channelId)?.type;
      const kind = type === "stage" ? "stage" : "voice";
      // The stage audience listens only; the microphone is requested when invited to speak.
      await enter({ kind, id: channelId, communityId, name }, kind === "voice", (ack) => getSocket()?.emit("voice:join", { channelId }, ack));
    },
    [enter, qc],
  );

  const joinCall = useCallback(
    async (conversationId: string, name: string) => {
      useCallStore.getState().stopRing(conversationId);
      if (roomRef.current?.kind === "call" && roomRef.current.id === conversationId) return;
      await enter({ kind: "call", id: conversationId, communityId: null, name }, true, (ack) =>
        getSocket()?.emit("call:join", { conversationId }, (res) => {
          if (res.ok && res.data) useCallStore.getState().setCall(conversationId, res.data.call);
          ack(res);
        }),
      );
    },
    [enter],
  );

  const toggleMute = useCallback(() => {
    if (state.current.serverMuted) {
      const r = roomRef.current;
      const self = participantsRef.current.find((p) => p.socketId === getSocket()?.id);
      toast.show(r?.kind === "stage" && !self?.speaker ? "You're in the audience. Raise your hand to ask to speak." : "A moderator has muted you in this channel");
      return;
    }
    const next = !state.current.muted;
    setMutedBoth(next);
    let deaf = state.current.deafened;
    if (!next && deaf) {
      deaf = false;
      setDeafened(false);
      state.current.deafened = false;
      mesh.current?.applySilence();
    }
    applyMic();
    getSocket()?.emit("voice:state", { muted: next, deafened: deaf });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDeafen = useCallback(() => {
    const next = !state.current.deafened;
    setDeafened(next);
    state.current.deafened = next;
    mesh.current?.applySilence();
    applyMic();
    getSocket()?.emit("voice:state", { muted: next || state.current.muted, deafened: next });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopShare = useCallback(() => {
    if (stopScreenLocal()) getSocket()?.emit("voice:stream", { streaming: false }, () => {});
  }, [stopScreenLocal]);

  /** Get a stream ready to send: content hint, and the browser's own "Stop sharing" button ending it. */
  const prepareScreen = useCallback(
    (stream: MediaStream, fps: StreamSettings["fps"]) => {
      const video = stream.getVideoTracks()[0];
      if (!video) return;
      if ("contentHint" in video) video.contentHint = contentHintFor(fps);
      video.addEventListener("ended", () => {
        if (screen.current === stream) stopShare();
      });
    },
    [stopShare],
  );

  const publishSettings = useCallback((settings: StreamSettings) => {
    shareRef.current = settings;
    setShareSettings(settings);
    getSocket()?.emit("voice:stream", { streaming: true, settings }, () => {});
  }, []);

  const startShare = useCallback(
    async (choice: StreamChoice) => {
      const r = roomRef.current;
      const s = getSocket();
      if (!r || !s || screen.current) return;
      if (!canShareScreen()) {
        toast.error("This browser can't share its screen.");
        return;
      }
      saveStreamChoice(choice);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions(choice));
      } catch (e) {
        if (!pickerCancelled(e)) toast.error("Couldn't start screen sharing");
        return;
      }
      const settings: StreamSettings = { resolution: choice.resolution, fps: choice.fps, audio: stream.getAudioTracks().length > 0 };
      const res = await new Promise<{ ok: boolean; error?: string }>((resolve) => s.emit("voice:stream", { streaming: true, settings }, (x) => resolve(x)));
      if (!res.ok || roomRef.current !== r || screen.current) {
        stream.getTracks().forEach((t) => t.stop());
        if (!res.ok) toast.error(res.error ?? "Couldn't start screen sharing");
        else if (!screen.current) s.emit("voice:stream", { streaming: false }, () => {});
        return;
      }
      prepareScreen(stream, choice.fps);
      screen.current = stream;
      shareRef.current = settings;
      setShareSettings(settings);
      setLocalScreen(stream);
      mesh.current?.setScreen(stream, settings);
    },
    [prepareScreen],
  );

  const changeShareSource = useCallback(async () => {
    const cur = shareRef.current;
    if (!screen.current || !cur) return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions({ ...cur, audio: loadStreamChoice().audio }));
    } catch (e) {
      if (!pickerCancelled(e)) toast.error("Couldn't change what you're sharing");
      return;
    }
    const old = screen.current;
    const live = shareRef.current;
    // Stopped (or left) while the picker was open.
    if (!old || !live) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    prepareScreen(stream, live.fps);
    screen.current = stream;
    setLocalScreen(stream);
    mesh.current?.replaceScreen(stream);
    old.getTracks().forEach((t) => t.stop());
    const audio = stream.getAudioTracks().length > 0;
    if (audio !== live.audio) publishSettings({ ...live, audio });
  }, [prepareScreen, publishSettings]);

  const updateShare = useCallback(
    async (choice: StreamChoice) => {
      const stream = screen.current;
      if (!stream || !shareRef.current) return;
      saveStreamChoice(choice);
      const video = stream.getVideoTracks()[0];
      if (video) {
        await video.applyConstraints(videoConstraints(choice)).catch(() => {});
        if ("contentHint" in video) video.contentHint = contentHintFor(choice.fps);
      }
      if (screen.current !== stream) return;
      // Audio can only be switched on if the captured surface has it.
      const tracks = stream.getAudioTracks();
      tracks.forEach((t) => (t.enabled = choice.audio));
      mesh.current?.retuneScreen(choice);
      publishSettings({ resolution: choice.resolution, fps: choice.fps, audio: choice.audio && tracks.length > 0 });
    },
    [publishSettings],
  );

  // While live, send a small snapshot now and then so people can see what's on before they watch.
  useEffect(() => {
    if (!localScreen) return;
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = localScreen;
    void video.play().catch(() => {});
    const send = async () => {
      const image = await snapshot(video);
      if (image && screen.current === localScreen) getSocket()?.emit("voice:preview", { image });
    };
    const first = setTimeout(() => void send(), 1500);
    const t = setInterval(() => void send(), PREVIEW_EVERY_MS);
    return () => {
      clearTimeout(first);
      clearInterval(t);
      video.srcObject = null;
    };
  }, [localScreen]);

  const setWatching = useCallback(
    (socketId: string, on: boolean) => {
      const ui = useStreamUi.getState();
      if (on) {
        watchingRef.current.add(socketId);
        // Watching a stream brings it to the front, unless you're already focused on another.
        if (!ui.focused || !watchingRef.current.has(ui.focused)) ui.setFocused(socketId);
      } else {
        if (watchingRef.current.delete(socketId)) mesh.current?.dropRemoteScreen(socketId);
        if (ui.focused === socketId) ui.setFocused(null);
      }
      setWatchingState(new Set(watchingRef.current));
      syncWatch();
    },
    [syncWatch],
  );

  // Keybinds dispatched by the app shell, and push-to-talk.
  useEffect(() => {
    const onVoice = (e: Event) => {
      if (!roomRef.current) return;
      const d = (e as CustomEvent<string>).detail;
      if (d === "toggleMute") toggleMute();
      else if (d === "toggleDeafen") toggleDeafen();
    };
    const setPtt = (v: boolean) => {
      if (state.current.talking === v) return;
      state.current.talking = v;
      setTalking(v);
      applyMic();
    };
    const down = (e: KeyboardEvent) => {
      const prefs = getPrefs(qc);
      if (!prefs.pushToTalk || !roomRef.current || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (matchesKey(e, prefs.pushToTalkKey)) setPtt(true);
    };
    const up = (e: KeyboardEvent) => {
      const prefs = getPrefs(qc);
      if (prefs.pushToTalk && e.key.toLowerCase() === (prefs.pushToTalkKey.split("+").pop() ?? "").toLowerCase()) setPtt(false);
    };
    const blur = () => setPtt(false);
    window.addEventListener("atrium:voice", onVoice);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("atrium:voice", onVoice);
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, [toggleMute, toggleDeafen]); // eslint-disable-line react-hooks/exhaustive-deps

  const selfSocketId = getSocket()?.id ?? null;
  const self = useMemo(() => participants.find((p) => p.socketId === selfSocketId) ?? null, [participants, selfSocketId]);

  const value = useMemo<VoiceCtx>(
    () => ({
      room,
      channelId: room && room.kind !== "call" ? room.id : null,
      communityId: room?.communityId ?? null,
      channelName: room?.name ?? null,
      callId: room?.kind === "call" ? room.id : null,
      status,
      muted,
      deafened,
      serverMuted,
      talking,
      speaking,
      selfSocketId,
      participants,
      self,
      localScreen,
      shareSettings,
      shareHasAudio: (localScreen?.getAudioTracks().length ?? 0) > 0,
      screens,
      watching,
      setWatching,
      join,
      joinCall,
      leave,
      toggleMute,
      toggleDeafen,
      startShare,
      changeShareSource,
      updateShare,
      stopShare,
    }),
    [room, status, muted, deafened, serverMuted, talking, speaking, selfSocketId, participants, self, localScreen, shareSettings, screens, watching, setWatching, join, joinCall, leave, toggleMute, toggleDeafen, startShare, changeShareSource, updateShare, stopShare],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <IncomingCalls />
      <GoLiveDialog />
      <MiniPlayer />
      <div ref={container} hidden aria-hidden />
    </Ctx.Provider>
  );
}

export function useVoice() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useVoice outside VoiceProvider");
  return ctx;
}
